/**
 * AgentVault tool calls → `ai.agent.tool.invoke` spans for the owner's
 * "Recent problems" list (agent Health page, read from `agent_telemetry_spans`).
 *
 * The span goes through the channel's EXISTING `TelemetryReporter`
 * (`SecureChannel.telemetry`: device JWT, hub-bound, buffered, flushed every
 * 30 s to `POST /api/v1/telemetry/ingest`). No new endpoint.
 *
 * PRIVACY CONTRACT — a span built here carries exactly these attributes:
 * `tool.name`, `tool.outcome`, `tool.error_kind` (errors only),
 * `av.conversation_kind`, `av.conversation_id` (only when known),
 * `tool.duration_ms`. Never message text, tool arguments, bodies, URLs,
 * tokens, exception messages or stacks. `tool.error_kind` is one of
 * `KNOWN_TOOL_ERROR_CODES` or an exception class name ending in `Error` /
 * `Exception`; anything else becomes `"unclassified"`. (The reporter's serializer adds `w3c.traceparent` /
 * `w3c.tracestate` — random trace/span ids — to every span it sends.)
 *
 * Shared by the OpenClaw entry and the room bridges (exported from index.ts).
 * Every function that reports is best-effort and never throws.
 */

import { AsyncLocalStorage } from "node:async_hooks";
import { randomBytes } from "node:crypto";
import type { TelemetrySpan } from "@agentvault/crypto";
import type { DeliveryTarget } from "./types.js";

export const TOOL_INVOKE_SPAN_NAME = "ai.agent.tool.invoke";
export const UNCLASSIFIED_ERROR = "unclassified";

/**
 * Every machine code a TypeScript client (OpenClaw entry, local /send API,
 * the room bridges) can put in `tool.error_kind`, besides an exception class
 * name. One list: `safeErrorKind` passes nothing else, and a test fails if a
 * call site or the text classifier below produces a code missing here.
 * Mirrors `docs/contracts/agent-tool-error-codes.json` (code → kind).
 */
export const KNOWN_TOOL_ERROR_CODES = [
  "relay_not_connected",
  "mls_group_not_initialized",
  "mls_encrypt_failed",
  "room_not_found",
  "a2a_channel_not_found",
  "channel_not_ready",
  "channel_not_started",
  "no_active_sessions",
  "no_conversation_ready",
  "blocked_by_policy",
  "unknown_target",
  "unsupported_destination",
  "delivery_timeout",
  "delivery_cancelled",
  "delivery_failed",
  "ambiguous_target",
  "no_receipt",
  "invalid_request",
  UNCLASSIFIED_ERROR,
] as const;
export type ToolErrorCode = (typeof KNOWN_TOOL_ERROR_CODES)[number];
const KNOWN_CODES: ReadonlySet<string> = new Set(KNOWN_TOOL_ERROR_CODES);

export type ToolConversationKind = "room" | "direct" | "unknown";

export interface ToolConversation {
  kind: ToolConversationKind;
  /** Room id or 1:1 conversation id. Ignored when `kind` is `"unknown"`. */
  id?: string;
}

export interface ToolInvokeReport {
  toolName: string;
  /** `cancelled`: the call was stopped on purpose (shutdown, superseded) —
   *  recorded with an OK status and no error kind; not a problem. */
  outcome: "success" | "error" | "cancelled";
  /** Machine code or exception class name. Sanitized; free text is dropped. */
  errorKind?: string;
  durationMs: number;
  /** Defaults to the conversation being handled (`currentToolConversation`). */
  conversation?: ToolConversation;
  /** Epoch ms the call ended (default: now). */
  endTime?: number;
}

/** The slice of `TelemetryReporter` this module needs. */
export interface ToolSpanSink {
  reportCustomSpan(span: TelemetrySpan): void;
}

// Exception class names: one identifier, short, ending Error/Exception. (A
// hex token cannot end in "Error"/"Exception" — r, o, x, p, t, i, n are not
// hex digits — so no separate hex check is needed; the tests pin that.)
const CLASS_NAME_RE = /^(?:[A-Za-z][A-Za-z0-9_]{0,47})?(?:Error|Exception)$/;
const TOOL_NAME_RE = /^[A-Za-z0-9_.:-]{1,64}$/;

/**
 * `value` when it is a known code (`KNOWN_TOOL_ERROR_CODES`) or an exception
 * class name, else `"unclassified"`. A handle, room name or token that
 * happens to be one identifier is NOT passed: only listed codes are.
 */
export function safeErrorKind(value: unknown): string {
  if (typeof value !== "string") return UNCLASSIFIED_ERROR;
  if (KNOWN_CODES.has(value)) return value;
  if (!CLASS_NAME_RE.test(value)) return UNCLASSIFIED_ERROR;
  if ((value.match(/[0-9]/g) ?? []).length > 3) return UNCLASSIFIED_ERROR;
  return value;
}

/**
 * Known send/delivery failure texts (channel.ts `send`, `sendToRoom`,
 * `deliver`, the bridges' delivery timeouts) → stable machine codes. Order
 * matters: `sendToRoom` wraps "relay socket is not open" inside its
 * "MLS encrypt failed … Error: …" message.
 */
export const ERROR_TEXT_CODES: ReadonlyArray<readonly [RegExp, ToolErrorCode]> = [
  [/relay socket is not open/, "relay_not_connected"],
  [/has no initialized MLS group/, "mls_group_not_initialized"],
  [/^MLS encrypt failed/, "mls_encrypt_failed"],
  [/^Room .+ not found$/, "room_not_found"],
  [/^No A2A channel found|^A2A channel .+ not found$/, "a2a_channel_not_found"],
  [/^Channel is not ready$|^Channel is in \w+ state$/, "channel_not_ready"],
  [/^AgentVault channel not connected$|^Channel not started$/, "channel_not_started"],
  [/^No active sessions$/, "no_active_sessions"],
  [/^No conversation is ready to receive/, "no_conversation_ready"],
  [/^Message blocked by scan rule/, "blocked_by_policy"],
  [/^Unknown target kind$/, "unknown_target"],
  [/can only be sent to owner|cannot be sent to A2A targets/, "unsupported_destination"],
  [/^Delivery timed out$/, "delivery_timeout"],
  [/^Delivery cancelled$/, "delivery_cancelled"],
  [/^Delivery failed$/, "delivery_failed"],
];

function codeForText(text: string): ToolErrorCode | undefined {
  // `String(err)` (what several callers put in `error`) prefixes "Error: ".
  const t = text.replace(/^[A-Za-z]*Error: /, "");
  for (const [re, code] of ERROR_TEXT_CODES) if (re.test(t)) return code;
  return undefined;
}

/**
 * A thrown value → `tool.error_kind`: a known failure's code, else the
 * exception's CLASS name (`constructor.name` — `err.name` is a writable
 * string anyone can set). The message itself is only matched, never returned.
 */
export function classifyToolError(err: unknown): string {
  try {
    if (err instanceof Error) {
      return codeForText(err.message) ?? safeErrorKind(err.constructor?.name);
    }
    if (typeof err === "string") return codeForText(err) ?? UNCLASSIFIED_ERROR;
  } catch { /* fall through */ }
  return UNCLASSIFIED_ERROR;
}

/** A `{ ok: false, error }` delivery receipt → `tool.error_kind`. */
export function classifyDeliveryReceipt(receipt: { error?: unknown; ambiguous?: boolean } | null | undefined): ToolErrorCode {
  if (!receipt) return "no_receipt";
  if (receipt.ambiguous) return "ambiguous_target";
  if (typeof receipt.error === "string") return codeForText(receipt.error) ?? "delivery_failed";
  return "delivery_failed";
}

// -- Conversation being handled ----------------------------------------------

/** One turn's conversation; `ended` once the turn returned (#1418 pattern). */
interface ToolConversationStore {
  conversation: ToolConversation;
  ended: boolean;
}

const _conversation = new AsyncLocalStorage<ToolConversationStore>();

/**
 * Run `fn` as the handling of an inbound message in `conversation`, so a tool
 * call made during it reports that conversation. Never changes what `fn` does.
 *
 * Work the turn leaves behind (a timer, an interval, a background job) still
 * sees this store, so it is marked ended when `fn` returns: after that the
 * work is nobody's turn and reports `unknown` — never a guess.
 */
export async function runWithToolConversation<T>(
  conversation: ToolConversation,
  fn: () => Promise<T>,
): Promise<T> {
  const store: ToolConversationStore = {
    conversation:
      conversation.kind === "unknown" || !conversation.id
        ? { kind: "unknown" }
        : { kind: conversation.kind, id: conversation.id },
    ended: false,
  };
  try {
    return await _conversation.run(store, fn);
  } finally {
    store.ended = true;
  }
}

/**
 * The conversation this call chain is handling while its turn runs;
 * `{ kind: "unknown" }` outside any turn or after the turn ended.
 */
export function currentToolConversation(): ToolConversation {
  const store = _conversation.getStore();
  if (!store || store.ended) return { kind: "unknown" };
  return store.conversation;
}

/**
 * The conversation an EXPLICITLY targeted send belongs to. A room target is
 * that room. An A2A target is not a room or 1:1. An owner target goes to the
 * person involved — the running turn's person when that turn is a 1:1, else
 * unknown. Only an untargeted / `context` send (or one whose target did not
 * parse) falls back to the turn being handled.
 */
export function conversationForTarget(
  target: DeliveryTarget | undefined,
  opts: { conversationId?: string } = {},
): ToolConversation {
  if (typeof opts.conversationId === "string" && opts.conversationId) {
    return { kind: "direct", id: opts.conversationId };
  }
  const turn = currentToolConversation();
  if (!target || target.kind === "context") return turn;
  switch (target.kind) {
    case "room":
      return target.roomId ? { kind: "room", id: target.roomId } : { kind: "unknown" };
    case "owner":
      return turn.kind === "direct" ? turn : { kind: "unknown" };
    default:
      return { kind: "unknown" };
  }
}

// -- Span ---------------------------------------------------------------------

export function buildToolInvokeSpan(report: ToolInvokeReport): TelemetrySpan {
  const isError = report.outcome === "error";
  const outcome = isError ? "error" : report.outcome === "cancelled" ? "cancelled" : "success";
  const durationMs = Math.max(0, Math.round(Number(report.durationMs) || 0));
  const endTime = report.endTime ?? Date.now();
  const conv = report.conversation ?? currentToolConversation();
  const kind: ToolConversationKind =
    conv.kind === "room" || conv.kind === "direct" ? conv.kind : "unknown";

  const attributes: Record<string, string | number | boolean> = {
    "tool.name": TOOL_NAME_RE.test(report.toolName) ? report.toolName : "unknown",
    "tool.outcome": outcome,
    "av.conversation_kind": kind,
    "tool.duration_ms": durationMs,
  };
  if (isError) attributes["tool.error_kind"] = safeErrorKind(report.errorKind);
  if (kind !== "unknown" && typeof conv.id === "string" && conv.id) {
    attributes["av.conversation_id"] = conv.id.slice(0, 64);
  }

  return {
    traceId: randomBytes(16).toString("hex"),
    spanId: randomBytes(8).toString("hex"),
    name: TOOL_INVOKE_SPAN_NAME,
    kind: "internal",
    startTime: endTime - durationMs,
    endTime,
    attributes,
    // OTLP codes: 1 = ok, 2 = error (the backend stores "ok" / "error").
    // A cancellation is not a problem: OK status, outcome "cancelled".
    status: { code: isError ? 2 : 1 },
  };
}

/** Queue one tool-call span on `sink`. Never throws; a missing sink is a no-op. */
export function reportToolInvoke(sink: ToolSpanSink | null | undefined, report: ToolInvokeReport): void {
  try {
    if (!sink || typeof sink.reportCustomSpan !== "function") return;
    sink.reportCustomSpan(buildToolInvokeSpan(report));
  } catch {
    // telemetry must never break a tool call
  }
}
